import styles from './PackageDashboard.module.css';
import React, { useState, useEffect } from 'react';
import { collection, getDocs, getDoc, doc, deleteDoc, query, where } from 'firebase/firestore';
import { db, auth } from '../../firebase';
import { useNavigate } from 'react-router-dom';
import { useAuthState } from 'react-firebase-hooks/auth';
import logo from '../../pacePackPrime.png';
import PrintDailySummary from '../../PrintDailySummary';

// Internal StatusFilter component
const StatusFilter = ({ statusFilter, setStatusFilter, statusTypes = [] }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  
  const handleSelect = (id) => {
    setStatusFilter(id);
    setIsDropdownOpen(false);
  };

  // Find the currently selected status with fallback
  const selectedStatus = statusFilter === 'all' 
    ? { id: 'all', label: 'All', color: 'bg-gray-200 text-gray-800', icon: null }
    : statusTypes.find(status => status.id === statusFilter) || { id: statusFilter, label: statusFilter, color: 'bg-gray-100 text-gray-800', icon: null };

  return (
    <div className="bg-gray-50 border-t border-gray-200 p-2 md:p-3">
      {/* Mobile Dropdown (visible on small screens) */}
      <div className="md:hidden">
        <div className="flex items-center justify-center">
          <span className="text-xs font-medium text-gray-500 mr-2">Filter by Status:</span>
          
          <div className="relative flex-1 max-w-xs">
            <button
              onClick={() => setIsDropdownOpen(!isDropdownOpen)}
              className="flex items-center justify-between w-full px-3 py-1.5 bg-white border border-gray-200 rounded-md shadow-sm text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              <div className="flex items-center gap-1">
                {selectedStatus.icon}
                <span>{selectedStatus.label}</span>
              </div>
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </button>
            
            {isDropdownOpen && (
              <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 overflow-auto ring-1 ring-black ring-opacity-5">
                <button
                  onClick={() => handleSelect('all')}
                  className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 ${
                    statusFilter === 'all' ? 'bg-gray-200 text-gray-800' : ''
                  }`}
                >
                  All
                </button>
                
                {statusTypes.map(status => (
                  <button
                    key={status.id}
                    onClick={() => handleSelect(status.id)}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-1 ${
                      statusFilter === status.id ? status.color : ''
                    }`}
                  >
                    {status.icon}
                    <span>{status.label}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Desktop Buttons (visible on md screens and up) */}
      <div className="hidden md:flex flex-wrap items-center justify-center gap-2 md:gap-3">
        <span className="text-xs md:text-sm font-medium text-gray-500">Filter by Status:</span>
        
        <button
          onClick={() => setStatusFilter('all')}
          className={`px-2 md:px-3 py-1 rounded-full text-xs font-medium transition-colors
            ${statusFilter === 'all' 
              ? 'bg-gray-200 text-gray-800' 
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
        >
          All
        </button>
        
        {statusTypes.map(status => (
          <button
            key={status.id}
            onClick={() => setStatusFilter(status.id)}
            className={`flex items-center gap-1 px-2 md:px-3 py-1 rounded-full text-xs font-medium transition-colors
              ${statusFilter === status.id 
                ? status.color 
                : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
          >
            {status.icon}
            {status.label}
            <span className="hidden xs:inline">{status.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

const PackageDashboard = () => {
  const [packages, setPackages] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [isDeleting, setIsDeleting] = useState(false);
  const [packageToDelete, setPackageToDelete] = useState(null);
  // null until the current user's own role doc has loaded - packages aren't
  // fetched until this resolves, so drivers never briefly see everyone's packages.
  const [userRoles, setUserRoles] = useState(null);
  const [user] = useAuthState(auth);
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Admin/owner keeps its existing meaning (controls the delete button).
  // canViewAllPackages is broader - admin, sales, or owner all see every
  // package; anyone else (drivers) only ever see packages assigned to them.
  const isAdmin = Array.isArray(userRoles) && (userRoles.includes('admin') || userRoles.includes('owner'));
  const canViewAllPackages = Array.isArray(userRoles) &&
    (userRoles.includes('admin') || userRoles.includes('sales') || userRoles.includes('owner'));

  // Status types with icons and styling
  const statusTypes = [
    { 
      id: 'delivered', 
      label: 'Delivered', 
      color: 'bg-green-100 text-green-800',
      icon: (
        <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
      )
    },
    { 
      id: 'in-transit', 
      label: 'In Transit', 
      color: 'bg-blue-100 text-blue-800',
      icon: (
        <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path d="M9 17a2 2 0 11-4 0 2 2 0 014 0zM19 17a2 2 0 11-4 0 2 2 0 014 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16V6a1 1 0 00-1-1H4a1 1 0 00-1 1v10a1 1 0 001 1h1m8-1a1 1 0 01-1 1H9m4-1V8a1 1 0 011-1h2.586a1 1 0 01.707.293l3.414 3.414a1 1 0 01.293.707V16a1 1 0 01-1 1h-1m-6-1a1 1 0 001 1h1M5 17a2 2 0 104 0m-4 0a2 2 0 114 0m6 0a2 2 0 104 0m-4 0a2 2 0 114 0" />
        </svg>
      )
    },
    { 
      id: 'pending', 
      label: 'Pending', 
      color: 'bg-gray-100 text-gray-800',
      icon: (
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    },
    { 
      id: 'returned', 
      label: 'Returned', 
      color: 'bg-yellow-100 text-orange-800',
      icon: (
        <svg className="w-4 h-4 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
        </svg>
      )
    },
      { 
      id: 'in-store', 
      label: 'In Store', 
      color: 'bg-gray-100 text-gray-800',
      icon: (
        <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13.5 21v-7.5a.75.75 0 0 1 .75-.75h3a.75.75 0 0 1 .75.75V21m-4.5 0H2.36m11.14 0H18m0 0h3.64m-1.39 0V9.349M3.75 21V9.349m0 0a3.001 3.001 0 0 0 3.75-.615A2.993 2.993 0 0 0 9.75 9.75c.896 0 1.7-.393 2.25-1.016a2.993 2.993 0 0 0 2.25 1.016c.896 0 1.7-.393 2.25-1.015a3.001 3.001 0 0 0 3.75.614m-16.5 0a3.004 3.004 0 0 1-.621-4.72l1.189-1.19A1.5 1.5 0 0 1 5.378 3h13.243a1.5 1.5 0 0 1 1.06.44l1.19 1.189a3 3 0 0 1-.621 4.72M6.75 18h3.75a.75.75 0 0 0 .75-.75V13.5a.75.75 0 0 0-.75-.75H6.75a.75.75 0 0 0-.75.75v3.75c0 .414.336.75.75.75"></path>
        </svg>
      )
    },
      { 
      id: 'canceled', 
      label: 'Canceled', 
      color: 'bg-red-100 text-red-800',
      icon: (
        <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m20.25 7.5-.625 10.632a2.25 2.25 0 0 1-2.247 2.118H6.622a2.25 2.25 0 0 1-2.247-2.118L3.75 7.5m6 4.125 2.25 2.25m0 0 2.25 2.25M12 13.875l2.25-2.25M12 13.875l-2.25 2.25M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125Z"></path>
        </svg>
      )
    },
      { 
      id: 'left-store', 
      label: 'Left Store', 
      color: 'bg-blue-100 text-blue-800',
      icon: (
        <svg className="w-4 h-4 text-red-500" fill="blue" stroke="currentColor" xmlns="http://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px">
          <path d="M200-120q-50 0-85-35t-35-85q0-50 35-85t85-35h560q50 0 85 35t35 85q0 50-35 85t-85 35H200Zm0-80h560q17 0 28.5-11.5T800-240q0-17-11.5-28.5T760-280H200q-17 0-28.5 11.5T160-240q0 17 11.5 28.5T200-200Zm200-240q-17 0-28.5-11.5T360-480v-320q0-17 11.5-28.5T400-840h320q17 0 28.5 11.5T760-800v320q0 17-11.5 28.5T720-440H400Zm40-80h240v-240H440v240Zm-360-2v-77h197v77H80Zm400-118h160v-79H480v79Zm-320 0h117v-79H160v79Zm280 120v-240 240Z"/>
        </svg>
      )
    },
      { 
      id: 'paid', 
      label: 'Paid', 
      color: 'bg-purple-100 text-purple-800',
      icon: (
        <svg className="w-4 h-4 text-purple-500" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24px" height="24px">
          <path d="M12 6v12m-3-2.818.879.659c1.171.879 3.07.879 4.242 0 1.172-.879 1.172-2.303 0-3.182C13.536 12.219 12.768 12 12 12c-.725 0-1.45-.22-2.003-.659-1.106-.879-1.106-2.303 0-3.182s2.9-.879 4.006 0l.415.33M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/>
        </svg>
      )
    },
      { 
      id: 'returning-to-store', 
      label: 'Returning to Store', 
      color: 'bg-yellow-100 text-yellow-800',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 text-yellow-500">
          <path fillRule="evenodd" d="M4.755 10.059a7.5 7.5 0 0 1 12.548-3.364l1.903 1.903h-3.183a.75.75 0 1 0 0 1.5h4.992a.75.75 0 0 0 .75-.75V4.356a.75.75 0 0 0-1.5 0v3.18l-1.9-1.9A9 9 0 0 0 3.306 9.67a.75.75 0 1 0 1.45.388Zm15.408 3.352a.75.75 0 0 0-.919.53 7.5 7.5 0 0 1-12.548 3.364l-1.902-1.903h3.183a.75.75 0 0 0 0-1.5H2.984a.75.75 0 0 0-.75.75v4.992a.75.75 0 0 0 1.5 0v-3.18l1.9 1.9a9 9 0 0 0 15.059-4.035.75.75 0 0 0-.53-.918Z" clipRule="evenodd" />
        </svg>
      )
    },
  ];

  // Fetch the current user's own role doc first. Packages are scoped off of
  // this, so it has to resolve before we decide what to query.
  useEffect(() => {
    const fetchCurrentUserRole = async () => {
      if (!user) {
        setUserRoles(null);
        return;
      }
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        const roles = userDoc.exists() ? (userDoc.data().roles || []) : [];
        setUserRoles(Array.isArray(roles) ? roles : []);
      } catch (error) {
        console.error("Error fetching current user's role:", error);
        setUserRoles([]); // fail closed - treat as no elevated role
      }
    };

    fetchCurrentUserRole();
  }, [user]);

  // Fetch packages from Firestore - admin/sales/owner see everything, drivers
  // (or anyone without one of those roles) only see packages assigned to them.
  useEffect(() => {
    const fetchPackages = async () => {
      if (!user || userRoles === null) return; // wait until the role is known

      try {
        const packagesRef = collection(db, 'packages');
        const packagesQuery = canViewAllPackages
          ? packagesRef
          : query(packagesRef, where('assignedTo', '==', user.uid));

        const querySnapshot = await getDocs(packagesQuery);
        const pkgList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPackages(pkgList);
      } catch (error){
        console.error("Error fetching packages:", error);
      }
    };

    fetchPackages();
  }, [user, userRoles, canViewAllPackages]);
  
  // Handle viewing package details
  const handleViewPackage = (packageId) => {
    navigate(`/package/${packageId}`);
  };


  // Handle confirming package deletion
  const handleDeleteClick = (e, pkg) => {
    e.stopPropagation();
    setPackageToDelete(pkg);
    setIsDeleting(true);
  };

  // Handle actual package deletion
  const confirmDelete = async () => {
    try {
      if (!user) {
        alert("You must be logged in to delete packages");
        return;
      }

      // Use isAdmin state from users collection
      if (!isAdmin) {
        alert("You don't have permission to delete packages");
        return;
      }

      // Delete the package from Firestore
      await deleteDoc(doc(db, 'packages', packageToDelete.id));

      // Update the local state to remove the deleted package
      setPackages(packages.filter(pkg => pkg.id !== packageToDelete.id));

      // Reset deletion state
      setPackageToDelete(null);
      setIsDeleting(false);
    } catch (error) {
      console.error("Error deleting package:", error);
      alert("Failed to delete package. Please try again.");
    }
  };

  // Cancel package deletion
  const cancelDelete = () => {
    setPackageToDelete(null);
    setIsDeleting(false);
  };

  // Filter packages based on search input and status filter
  const filteredPackages = packages.filter(pkg => {
    // Skip packages without tracking numbers
    if (!pkg.trackingNumber) return false;
    
    // Apply text search filter - search across multiple fields
    const searchTerm = search.toLowerCase();
    const matchesSearch = searchTerm === '' || 
      (pkg.trackingNumber && pkg.trackingNumber.toLowerCase().includes(searchTerm)) ||
      (pkg.recipient && pkg.recipient.toLowerCase().includes(searchTerm)) ||
      (pkg.destination && pkg.destination.toLowerCase().includes(searchTerm)) ||
      (pkg.phone && pkg.phone.toLowerCase().includes(searchTerm));
    
    // Apply status filter if not "all"
    const matchesStatus = statusFilter === 'all' || pkg.status.toLowerCase() === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  // Sort packages based on selected criteria
  const sortedPackages = [...filteredPackages].sort((a, b) => {
    let aValue, bValue;
    switch (sortBy) {
      case 'recipient':
        aValue = a.recipient || '';
        bValue = b.recipient || '';
        break;
      case 'destination':
        aValue = a.destination || '';
        bValue = b.destination || '';
        break;
      case 'description':
        aValue = a.details || a.description || '';
        bValue = b.details || b.description || '';
        break;
      case 'phone':
        aValue = a.phone || '';
        bValue = b.phone || '';
        break;
      case 'trackingNumber':
        aValue = a.trackingNumber || '';
        bValue = b.trackingNumber || '';
        break;
      case 'status':
        aValue = a.status || '';
        bValue = b.status || '';
        break;
      case 'createdAt':
      default:
        aValue = a.createdAt?.seconds || 0;
        bValue = b.createdAt?.seconds || 0;
        break;
    }
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Get status info for a package
  const getStatusInfo = (status) => {
    return statusTypes.find(type => type.id === status.toLowerCase() ) || statusTypes[2]; // Default to pending
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('desc');
    }
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return <span className="ml-1 text-gray-300">↕</span>;
    return sortOrder === 'asc'
      ? <span className="ml-1 text-indigo-600">▲</span>
      : <span className="ml-1 text-indigo-600">▼</span>;
  };

  const totalPages = Math.ceil(sortedPackages.length / pageSize);
  const paginatedPackages = sortedPackages.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  useEffect(() => {
    setCurrentPage(1); // Reset to first page when filters/search change
  }, [search, statusFilter, sortBy, sortOrder, pageSize]);

  return (
    <div className="w-full flex justify-center">
      <div className="w-full max-w-full lg:max-w-7xl xl:max-w-[1400px] 2xl:max-w-[1600px] p-4 md:p-6 lg:px-8">
        <header className="flex flex-col sm:flex-row justify-between items-center mb-4 md:mb-6 gap-3">
          <img className={styles.headerLogo} src={logo} alt="Logo" />
          <button 
            onClick={() => navigate('/add')}
            className={styles.addPackageButton}
          >
            + Add Package
          </button>
        </header>

        {/* Search Bar */}
        <div className="bg-white shadow-sm rounded-lg mb-4 md:mb-6 overflow-visible relative z-30">
          <div className="p-3 md:p-4">
            <div className="flex flex-col sm:flex-row gap-2">
              <input
                type="text"
                placeholder="Search packages..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="flex-1 px-3 md:px-4 py-2.5 md:py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
              <button className="hidden sm:block px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 active:bg-blue-800 transition-colors">
                Search
              </button>
            </div>
          </div>
          
          {/* Status Filter Component */}
          <StatusFilter 
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            statusTypes={statusTypes}
          />
        </div>

        {/* Packages per page selector and pagination controls */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4 relative z-20">
          <div className="flex items-center">
            <label className="text-xs font-medium text-gray-600 mr-2">Packages per page:</label>
            <select
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {[20, 50, 100].map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Prev
            </button>
            <span className="text-sm font-medium text-gray-700 px-2">{currentPage} / {totalPages || 1}</span>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages || totalPages === 0}
              className="px-4 py-2 rounded-lg bg-white border border-gray-300 text-gray-700 text-sm font-medium hover:bg-gray-50 active:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
          </div>
        </div>

        {/* Packages List */}
        {packages.length === 0 ? (
          <div className="bg-white shadow-sm rounded-lg p-8 text-center w-full">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <p className="mt-4 text-gray-500">No packages found. Add your first package to get started!</p>
          </div>
        ) : filteredPackages.length === 0 ? (
          <div className="bg-white shadow-sm rounded-lg p-8 text-center w-full">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <p className="mt-4 text-gray-500">No packages match your search criteria.</p>
          </div>
        ) : (
          <div className="bg-white shadow rounded w-full overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-auto min-w-max">
                <thead>
                  <tr className="bg-gray-100 text-left">
                    <th className="p-3 whitespace-nowrap">
                      <div className="flex items-center cursor-pointer" onClick={() => handleSort('createdAt')}>
                        Date Created
                        {getSortIcon('createdAt')}
                      </div>
                    </th>
                    <th className="p-3 whitespace-nowrap">
                      <div className="flex items-center cursor-pointer" onClick={() => handleSort('recipient')}>
                        Recipient
                        {getSortIcon('recipient')}
                      </div>
                    </th>
                    <th className="p-3 whitespace-nowrap">
                      <div className="flex items-center cursor-pointer" onClick={() => handleSort('destination')}>
                        Destination
                        {getSortIcon('destination')}
                      </div>
                    </th>
                    <th className="p-3 whitespace-nowrap">
                      <div className="flex items-center cursor-pointer" onClick={() => handleSort('phone')}>
                        Phone
                        {getSortIcon('phone')}
                      </div>
                    </th>
                    <th className="p-3 whitespace-nowrap">
                      <div className="flex items-center cursor-pointer" onClick={() => handleSort('status')}>
                        Status
                        {getSortIcon('status')}
                      </div>
                    </th>
                    <th className="p-3 whitespace-nowrap">
                      <div className="flex items-center cursor-pointer" onClick={() => handleSort('trackingNumber')}>
                        Tracking #
                        {getSortIcon('trackingNumber')}
                      </div>
                    </th>
                    {isAdmin && (
                      <th className="p-3 text-right whitespace-nowrap">Actions</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {paginatedPackages.map((pkg) => {
                    const statusInfo = getStatusInfo(pkg.status);
                    
                    return (
                      <tr 
                        key={pkg.id || pkg.trackingNumber}
                        onClick={() => handleViewPackage(pkg.id)} 
                        className="border-t hover:bg-gray-50 cursor-pointer"
                      >
                        <td className="p-3 whitespace-nowrap">
                          {pkg.createdAt?.seconds
                            ? new Date(pkg.createdAt.seconds * 1000).toLocaleString()
                            : 'N/A'}
                        </td>
                        <td className="p-3 whitespace-nowrap">{pkg.recipient || 'N/A'}</td>
                        <td className="p-3 whitespace-nowrap">{pkg.destination || 'N/A'}</td>
                        <td className="p-3 whitespace-nowrap">{pkg.phone || 'N/A'}</td>
                        <td className="p-3 whitespace-nowrap">
                          <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${statusInfo.color}`}>
                            {statusInfo.icon}
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="p-3 font-mono whitespace-nowrap">{pkg.trackingNumber || 'N/A'}</td>
                        {isAdmin && (
                          <td className="p-3 text-right whitespace-nowrap">
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={(e) => handleDeleteClick(e, pkg)}
                                className={styles.tableActionsButton}
                                title="Delete package"
                              >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Package count */}
        <div className="mt-4 text-xs md:text-sm text-gray-500 text-center">
          Showing {filteredPackages.length} of {packages.length} packages
        </div>

        <PrintDailySummary packages={packages} userId={user?.uid} />

        {/* Delete Confirmation Modal */}
        {isDeleting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className={styles.deleteModal}>
              <h3 className={styles.deleteModalTitle}>Confirm Delete</h3>
              <p className={styles.deleteModalText}>
                Are you sure you want to delete package with tracking number: 
                <span className="font-mono font-bold block mt-2">{packageToDelete?.trackingNumber}</span>
                This action cannot be undone.
              </p>
              <div className={styles.deleteModalActions}>
                <button
                  onClick={cancelDelete}
                  className={styles.cancelButton}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className={styles.confirmDeleteButton}
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PackageDashboard;